import { capitalize } from "@mui/material";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import ImageIcon from "../../Assets/images/VoxApp-Icon.png";
import { AssistantIcon } from "../../Assets/svgs/AssistantIcon";
import { Loader } from "../../Common/Loader";
import useDebounce from "../../Common/useDebounce";
import ChatSummaryDetailsFormModal from "../../Model/ChatSummaryDetailsFormModal";
import {
  getChatDetails,
  getWidgetChatDetails,
} from "../../Redux/Slice/Chats/ChatDetailsSlice";
import ChatSummaryList from "./ChatSummaryList";
import CloseRedIcon from "../../Assets/svgs/CloseRedIcon";
import MailIcon from "../../Assets/svgs/MailIcon";
import { ConfirmModel } from "../../Model/ConfirmModel";
import { ErrorModel } from "../../Model/ErrorModel";
import { SuccessModel } from "../../Model/SuccessModel";
import { scheduleInstantCall } from "../../Redux/Slice/Contacts/ScheduleInstantCallSlice";
import InstantEmailModal from "../../Model/InstantEmailModal";
import LowCreditModal from "../../Model/LowCreditModal";
import { isParsedTrue } from "../../Common/utils";

export const Chat = ({ noSelectedChat }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { userPlanFeatures } = useSelector((s) => s?.userPlanDetails);
  const isForMetaAssistant = location.pathname.includes("meta-assistant-chat");
  const selectedTabScreen = location?.pathname?.includes("/individual")
    ? "INDIVIDUAL"
    : location?.pathname?.includes("/group-chat")
    ? "GROUP_CHAT"
    : location?.pathname?.includes("/widget-chat")
    ? "WIDGET_CHAT"
    : "";
  const isCallExternal = location.pathname?.includes("/individual-external");
  const allPaths = location.pathname.split("/");
  const contactId = selectedTabScreen ? allPaths[allPaths.length - 2] : "";
  const assistantId = selectedTabScreen ? allPaths[allPaths.length - 1] : "";
  const isExternalFromEmail = isCallExternal
    ? selectedTabScreen
      ? isParsedTrue(allPaths[allPaths.length - 1])
      : false
    : false;
  const [searchText, setSearchText] = useState("");
  const searchQuery = useDebounce(searchText, 500);

  const [summaryFormPopup, setSummaryFormPopup] = useState({
    visible: false,
    data: {
      fromDate: "",
      toDate: "",
      time: "",
    },
  });
  const [isSearching, setIsSearching] = useState(false);
  const { chatDetailsData, loading } = useSelector(
    (state) => state?.chatDetails
  );
  const vapiAssistantId =
    chatDetailsData && chatDetailsData?.data
      ? chatDetailsData?.data?.assistant?.vapiAssistanceId
      : assistantId;
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [chatDetails, setChatDetails] = useState({});
  const [allChats, setAllChats] = useState([]);
  const chatContainerRef = useRef(null);
  const topRef = useRef(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [tab, setTab] = useState("");
  const [confirmModel, setConfirmModel] = useState({
    title: "",
    message: "",
    type: "",
    isVisible: false,
  });
  const [successModal, setSuccessModal] = useState({
    visible: false,
    message: "",
  });
  const [errorModal, setErrorModal] = useState({ visible: false, message: "" });
  const [showInstantEmailPopup, setShowInstantEmailPopup] = useState({
    visible: false,
    data: null,
  });
  const [showLowCreditModal, setShowLowCreditModal] = useState(false);

  const showSuccessMessage = (message, callback) => {
    setSuccessModal({
      visible: true,
      message:
        typeof message == "string"
          ? message
          : "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setSuccessModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 3000);
  };

  const showErrorMessage = (message, callback) => {
    setErrorModal({
      visible: true,
      message:
        typeof message == "string"
          ? message
          : "Something went wrong, please try again later!",
    });

    setTimeout(() => {
      setErrorModal({ visible: false, message: "" });
      if (typeof callback === "function") {
        callback();
      }
    }, 2000);
  };

  const closeSummaryDetailPopup = () => {
    setSummaryFormPopup((p) => ({
      ...p,
      visible: false,
    }));
  };

  const onSubmitSummaryDetail = (data) => {
    setIsSearching(false);
    setSearchText("");
    setSummaryFormPopup((p) => ({
      ...p,
      visible: false,
      data: data,
    }));
  };

  const onClearSummaryDetailFilter = (data) => {
    setSummaryFormPopup((p) => ({
      ...p,
      visible: false,
      data: {
        fromDate: "",
        toDate: "",
        time: "",
      },
    }));
  };

  const isSummaryFilterApplied = useMemo(() => {
    return (
      summaryFormPopup.data.fromDate ||
      summaryFormPopup.data.time ||
      summaryFormPopup.data.toDate
    );
  }, [summaryFormPopup]);

  const formattedMessage = useMemo(() => {
    return allChats
      .reduce((acc, item) => {
        // Find an existing entry for the date
        let dateEntry = acc.find((entry) => entry?.date === item?.date);

        // If there's no entry, create a new one
        if (!dateEntry) {
          dateEntry = { date: item?.date, messages: [] };
          acc.push(dateEntry);
        }

        // Add the message to the corresponding date's messages
        dateEntry?.messages.push(item);

        return acc;
      }, [])
      .map((entry) => ({
        ...entry,
        messages: entry?.messages?.sort(
          (a, b) => new Date(a?.time) - new Date(b?.time)
        ),
      }))
      .sort((a, b) => new Date(a?.date) - new Date(b?.date));
  }, [allChats]);

  const scrollToTop = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [chatContainerRef.current]);

  // Callback to load older messages when reaching the top
  const handleLoadMore = useCallback(async () => {
    if (!loading && page < totalPages) {
      const previousScrollHeight = chatContainerRef.current?.scrollHeight;
      const previousScrollTop = chatContainerRef.current?.scrollTop;
      const result = await dispatch(
        selectedTabScreen == "WIDGET_CHAT"
          ? getWidgetChatDetails({
              page: page + 1,
              widgetId: contactId,
              assistantId: assistantId,
              callType: tab,
            })
          : getChatDetails({
              page: page + 1,
              contactId: contactId,
              assistantId: assistantId,
              callType: tab,
              isCallExternal: isCallExternal,
              isExternalFromEmail: isExternalFromEmail,
            })
      );
      setAllChats((prev) => [...result?.payload?.data?.messages, ...prev]);
      setPage((prev) => prev + 1);
      setTotalPages(result?.payload?.pagination?.totalPages);
      setTotalItems(Number(result?.payload?.pagination?.totalItems ?? 0));

      setTimeout(() => {
        if (chatContainerRef.current) {
          // Calculate new scrollTop based on the difference in scroll height
          const newScrollHeight = chatContainerRef.current.scrollHeight;
          chatContainerRef.current.scrollTop =
            newScrollHeight - previousScrollHeight + previousScrollTop;
        }
      }, 0);
    }
  }, [page, totalPages, loading, setPage]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) handleLoadMore();
      },
      { root: chatContainerRef.current, threshold: 1 }
    );

    if (topRef.current) observer.observe(topRef.current);

    return () => {
      if (topRef.current) observer.unobserve(topRef.current);
    };
  }, [page, totalPages, loading, setPage]);

  const getChatData = async (page, isLoading) => {
    if (noSelectedChat) return;
    if (isLoading) {
      setIsLoading(true);
    }
    try {
      const action =
        selectedTabScreen == "WIDGET_CHAT"
          ? getWidgetChatDetails
          : getChatDetails;

      const result = await dispatch(
        selectedTabScreen == "WIDGET_CHAT"
          ? getWidgetChatDetails({
              page: page,
              widgetId: contactId,
              assistantId: assistantId,
              keyword: isSearching ? searchText : "",
              callType: tab,
            })
          : getChatDetails({
              page: page,
              contactId: contactId,
              assistantId: assistantId,
              keyword: isSearching ? searchText : "",
              callType: tab,
              isCallExternal: isCallExternal,
              isExternalFromEmail: isExternalFromEmail,
            })
      );
      if (action.fulfilled.match(result) && result?.payload?.success === true) {
        if (Array.isArray(result?.payload?.data?.messages)) {
          setAllChats(result?.payload?.data?.messages);
        } else {
          setAllChats([]);
        }
        setPage(page);
        setTotalPages(result?.payload?.pagination?.totalPages);
        setTotalItems(Number(result?.payload?.pagination?.totalItems ?? 0));
      }
    } catch (error) {
    } finally {
      if (isLoading) {
        setIsLoading(false);
      }
    }
  };

  const handleChangeSearch = (e) => {
    setSearchText(e?.target?.value);
  };

  const scheduleInstantContactCall = async () => {
    let body = {
      assistantId: vapiAssistantId,
      contactIds: [contactId],
    };
    setIsLoading(true);
    try {
      const result = await dispatch(scheduleInstantCall(body));
      if (
        scheduleInstantCall.fulfilled.match(result) &&
        result?.payload?.success === true
      ) {
        setIsLoading(false);
        showSuccessMessage("Call Scheduled Successfully!");
      } else {
        setIsLoading(false);
        showErrorMessage(result?.payload?.error?.message);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const onConfirmModal = () => {
    if (confirmModel.type == "CONFIRM_INSTANT_CALL") {
      scheduleInstantContactCall();
    }
    hideConfirmInstantCall();
  };

  const showConfirmInstantCall = () => {
    setConfirmModel({
      title: "Instant Call",
      message: "Are you sure you want to instant call?",
      type: "CONFIRM_INSTANT_CALL",
      isVisible: true,
    });
  };

  const hideConfirmInstantCall = () => {
    setConfirmModel({
      title: "",
      message: "",
      type: "",
      isVisible: false,
    });
  };

  const closeInstantCallPopup = (value = "default", error = null) => {
    setIsLoading(false);
    setShowInstantEmailPopup({
      visible: false,
      data: null,
    });
    if (value === "success") {
      showSuccessMessage("Email Sent successfully!");
    } else if (value === "error") {
      const apiErrorMessage =
        error && typeof error?.message == "string" ? error?.message : null;
      const apiErrorCode = error && error?.code ? error?.code : null;
      if (apiErrorCode == 1030) {
        setShowLowCreditModal(true);
      } else {
        showErrorMessage(apiErrorMessage ?? "Something went wrong.");
      }
    }
  };

  const showInstantEmailModal = () => {
    setShowInstantEmailPopup({
      visible: true,
      data: {
        id: contactId,
      },
    });
  };

  const ActionMenu = () => {
    const [summaryButton, setSumaryButton] = useState(false);
    const modalRef = useRef(null);

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setSumaryButton(false);
      }
    };

    useEffect(() => {
      document.addEventListener("click", handleClickOutside, true);
      return () => {
        document.removeEventListener("click", handleClickOutside, true);
      };
    }, []);

    return (
      <ul className="dropdown">
        <li className="dropdown__item">
          <button
            className="btn p_0"
            onClick={() => setSumaryButton((prevData) => !prevData)}
          >
            <svg
              width="5"
              height="20"
              viewBox="0 0 5 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 4C3.60457 4 4.5 3.10457 4.5 2C4.5 0.89543 3.60457 0 2.5 0C1.39543 0 0.5 0.89543 0.5 2C0.5 3.10457 1.39543 4 2.5 4Z"
                fill="#0F172A"
              />
              <path
                d="M2.5 12C3.60457 12 4.5 11.1046 4.5 10C4.5 8.89543 3.60457 8 2.5 8C1.39543 8 0.5 8.89543 0.5 10C0.5 11.1046 1.39543 12 2.5 12Z"
                fill="#0F172A"
              />
              <path
                d="M2.5 20C3.60457 20 4.5 19.1046 4.5 18C4.5 16.8954 3.60457 16 2.5 16C1.39543 16 0.5 16.8954 0.5 18C0.5 19.1046 1.39543 20 2.5 20Z"
                fill="#0F172A"
              />
            </svg>
          </button>
          {summaryButton ? (
            <ul className="dropdownMenu dropdownMenu--open" ref={modalRef}>
              <li
                className="dropdown__item"
                onClick={() => {
                  setSumaryButton(false);
                  //  setShowSummary(true)
                  setSummaryFormPopup((p) => ({
                    ...p,
                    visible: true,
                  }));
                }}
              >
                <span className="icon">
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.13333 7.99944C1.13333 7.99944 3.81212 2.28516 8.5 2.28516C13.1879 2.28516 15.8667 7.99944 15.8667 7.99944C15.8667 7.99944 13.1879 13.7137 8.5 13.7137C3.81212 13.7137 1.13333 7.99944 1.13333 7.99944Z"
                      stroke="#0F172A"
                      stroke-width="1.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M8.5 10.1423C9.60959 10.1423 10.5091 9.18291 10.5091 7.99944C10.5091 6.81597 9.60959 5.85658 8.5 5.85658C7.39041 5.85658 6.49091 6.81597 6.49091 7.99944C6.49091 9.18291 7.39041 10.1423 8.5 10.1423Z"
                      stroke="#0F172A"
                      stroke-width="1.4"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                View Summary
              </li>
              <li
                className="dropdown__item"
                onClick={() => {
                  setSumaryButton((prevData) => !prevData);
                  onClearSummaryDetailFilter();
                  setIsSearching(true);
                }}
              >
                <span className="icon">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15.75 15.75L12.4875 12.4875M14.25 8.25C14.25 11.5637 11.5637 14.25 8.25 14.25C4.93629 14.25 2.25 11.5637 2.25 8.25C2.25 4.93629 4.93629 2.25 8.25 2.25C11.5637 2.25 14.25 4.93629 14.25 8.25Z"
                      stroke="#0F172A"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                Search
              </li>
              {selectedTabScreen != "WIDGET_CHAT" &&
              !chatDetailsData?.data?.contact?.isContactDeleted &&
              !isCallExternal &&
              !isForMetaAssistant ? (
                <>
                  {userPlanFeatures?.OUT_BOUND_CALL ? (
                    <li
                      className="dropdown__item"
                      onClick={showConfirmInstantCall}
                    >
                      <span className="icon">
                        <svg
                          width="15"
                          height="18"
                          viewBox="0 0 15 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M8.76197 16.7238L8.76906 16.73C9.38109 17.2114 10.1078 17.4204 10.829 17.3226C11.5502 17.2247 12.2228 16.8258 12.7357 16.1918L13.1813 15.6415C13.28 15.5196 13.3583 15.3749 13.4118 15.2156C13.4652 15.0563 13.4927 14.8855 13.4927 14.7131C13.4927 14.5407 13.4652 14.3699 13.4118 14.2106C13.3583 14.0513 13.28 13.9066 13.1813 13.7847L11.3021 11.4651C11.2034 11.3431 11.0862 11.2464 10.9573 11.1804C10.8283 11.1144 10.6901 11.0804 10.5505 11.0804C10.4109 11.0804 10.2727 11.1144 10.1438 11.1804C10.0148 11.2464 9.89764 11.3431 9.79897 11.4651C9.59972 11.7112 9.32952 11.8494 9.04779 11.8494C8.76605 11.8494 8.49585 11.7112 8.2966 11.4651L5.29185 7.75247C5.09266 7.50634 4.98076 7.17256 4.98076 6.82453C4.98076 6.4765 5.09266 6.14273 5.29185 5.8966C5.39058 5.77471 5.46891 5.62998 5.52234 5.47069C5.57578 5.31139 5.60329 5.14065 5.60329 4.96822C5.60329 4.79579 5.57578 4.62505 5.52234 4.46575C5.46891 4.30646 5.39058 4.16173 5.29185 4.03984L3.41335 1.72022C3.2141 1.47416 2.9439 1.33594 2.66216 1.33594C2.38042 1.33594 2.11022 1.47416 1.91097 1.72022L1.46472 2.27059C0.951599 2.90425 0.628833 3.73519 0.549755 4.62612C0.470678 5.51705 0.640022 6.41465 1.02981 7.17059L1.03406 7.17934C3.0926 10.9417 5.71591 14.1816 8.76197 16.7238Z"
                            stroke="#0F172A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </span>
                      Instant Call
                    </li>
                  ) : null}
                  {userPlanFeatures?.WEB_CALLS ? (
                    <li
                      className="dropdown__item"
                      onClick={showInstantEmailModal}
                    >
                      <span className="icon">
                        <MailIcon />
                      </span>
                      Instant Mail
                    </li>
                  ) : null}
                </>
              ) : null}
            </ul>
          ) : (
            ""
          )}
        </li>
      </ul>
    );
  };

  const selectedTab = (currentTab) => {
    setTab(currentTab);
  };

  useEffect(() => {
    setAllChats([]);
    getChatData(1);
  }, [searchQuery]);

  useEffect(() => {
    setAllChats([]);
    getChatData(1, true);
  }, [tab]);

  useEffect(() => {
    if (assistantId) {
      setTab("");
      onClearSummaryDetailFilter();
      setIsSearching(false);
      setSearchText("");
      getChatData(1, true);
    }
  }, [contactId, assistantId]);

  useEffect(() => {
    scrollToTop();
  }, [chatContainerRef.current]);

  if (!chatDetailsData || noSelectedChat) {
    return (
      <div className="page__body">
        {isLoading ? <Loader /> : ""}
        <div className="no-data">Please select a chat to begin</div>
      </div>
    );
  }

  return (
    <div className="page__body">
      {isLoading ? <Loader /> : ""}
      {errorModal.visible && <ErrorModel message={errorModal.message} />}
      {successModal.visible && <SuccessModel message={successModal.message} />}
      {confirmModel.isVisible ? (
        <ConfirmModel
          onAccept={() => onConfirmModal()}
          onReject={hideConfirmInstantCall}
          yes={"Yes"}
          no={"No"}
          title={confirmModel.title}
          message={confirmModel.message}
        />
      ) : null}
      {showInstantEmailPopup.visible ? (
        <InstantEmailModal
          closePopup={closeInstantCallPopup}
          assistantId={vapiAssistantId}
          currentContact={{
            id: contactId,
          }}
          isForContactGroup={false}
        />
      ) : null}
      {showLowCreditModal ? (
        <LowCreditModal onClose={() => setShowLowCreditModal(false)} />
      ) : null}
      {summaryFormPopup.visible ? (
        <ChatSummaryDetailsFormModal
          data={summaryFormPopup.data}
          closePopup={closeSummaryDetailPopup}
          onSubmit={onSubmitSummaryDetail}
        />
      ) : null}
      <div className="chatsWrap">
        <div className="chatsWrap__head">
          <div className="chatsWrap__head__profile">
            <div className="initials">
              <img
                src={chatDetailsData?.data?.contact?.avatar ?? ImageIcon}
                alt=""
              />
            </div>
            <div className="info">
              <h4 className="name">
                {capitalize(
                  selectedTabScreen == "WIDGET_CHAT"
                    ? typeof chatDetailsData?.data?.widget?.domain == "string"
                      ? chatDetailsData?.data?.widget?.domain
                      : ""
                    : chatDetailsData?.data?.contact?.contactName
                    ? chatDetailsData?.data?.contact?.contactName
                    : ""
                )}
              </h4>
              {chatDetailsData?.data?.contact?.isContactDeleted ? (
                <p className="para para--danger">
                  <CloseRedIcon /> Deleted Contact
                </p>
              ) : isCallExternal ? (
                <p className="para para--primary">External</p>
              ) : selectedTabScreen == "WIDGET_CHAT" ? (
                <p className="para">{chatDetailsData?.data?.widget?.id}</p>
              ) : null}
            </div>
          </div>
          <div className="chatsWrap__head__action">
            <button className="btn p_0">
              <AssistantIcon />
              {chatDetailsData?.data?.assistant?.name}
            </button>
            <ActionMenu />
          </div>
        </div>
        {isSearching ? (
          <div className="chatsWrap__body" style={{ height: "110px" }}>
            <div className="chatsSearchResult">
              <div className="form__Field">
                <div className="form__groupField">
                  <span
                    className="btn"
                    onClick={() => {
                      setSearchText("");
                      setIsSearching((prevData) => !prevData);
                      getChatData(1, true);
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 8H1M1 8L8 15M1 8L8 1"
                        stroke="#0F172A"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="btn">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17.5 17.5L13.875 13.875M15.8333 9.16667C15.8333 12.8486 12.8486 15.8333 9.16667 15.8333C5.48477 15.8333 2.5 12.8486 2.5 9.16667C2.5 5.48477 5.48477 2.5 9.16667 2.5C12.8486 2.5 15.8333 5.48477 15.8333 9.16667Z"
                        stroke="#475569"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="form__input form__input--sm"
                    value={searchText}
                    onChange={handleChangeSearch}
                    placeholder="Search a Chat"
                  />
                </div>
              </div>
              <div className="chatsWrap__body__head">
                <h3 className="title">
                  All Results <span className="count">{totalItems}</span>
                </h3>
              </div>
            </div>
          </div>
        ) : null}
        {selectedTabScreen != "WIDGET_CHAT" && !isForMetaAssistant ? (
          <div className="chatsWrap__tabs">
            <div className="tabsWrap tabsWrap--second">
              <ul className="tabs">
                <li
                  className={
                    tab === "web"
                      ? "tabs__link tabs__link--selected"
                      : "tabs__link"
                  }
                  onClick={() => selectedTab("web")}
                >
                  Web Call
                </li>
                <li
                  className={
                    tab === "phone"
                      ? "tabs__link tabs__link--selected"
                      : "tabs__link"
                  }
                  onClick={() => selectedTab("phone")}
                >
                  Phone Call
                </li>
                <li
                  className={
                    tab === ""
                      ? "tabs__link tabs__link--selected"
                      : "tabs__link"
                  }
                  onClick={() => selectedTab("")}
                >
                  All
                </li>
              </ul>
            </div>
          </div>
        ) : null}
        {isSummaryFilterApplied ? (
          <ChatSummaryList
            contactId={contactId}
            assistantId={assistantId}
            summaryFormPopup={summaryFormPopup}
            isCallExternal={isCallExternal}
            isWidgetCall={selectedTabScreen == "WIDGET_CHAT"}
            domainName={
              typeof chatDetailsData?.data?.widget?.domain == "string"
                ? chatDetailsData?.data?.widget?.domain
                : ""
            }
            assistantName={chatDetailsData?.data?.assistant?.name}
            isExternalFromEmail={isExternalFromEmail}
            onClearSummaryDetailFilter={onClearSummaryDetailFilter}
          />
        ) : (
          <div ref={chatContainerRef} className="chatsWrap__body">
            <div ref={topRef} style={{ height: "1px" }}></div>
            {formattedMessage?.map((chat, index) => (
              <React.Fragment key={`${chat?.date}${index}`}>
                <p className="textDay">
                  <span>
                    {moment(chat?.date, "YYYY-MM-DD").isSame(moment(), "day")
                      ? "Today"
                      : moment(chat?.date, "YYYY-MM-DD")?.format("DD/MM/YYYY")}
                  </span>
                </p>
                {Array.isArray(chat?.messages)
                  ? chat?.messages?.map((message, messageIndex) =>
                      message?.role != "bot" ? (
                        <p
                          key={`message-${message?.time} ${messageIndex}`}
                          className="chatText"
                        >
                          <span className="chatText__value">
                            {message?.message}
                          </span>
                          <span className="chatText__time">
                            {moment(message?.time).isSame(moment(), "day")
                              ? `Today, ${moment(message?.time)?.format(
                                  "h:mma"
                                )}`
                              : moment(message?.time)?.format(
                                  "DD/MM/YYYY h:mma"
                                )}
                          </span>
                        </p>
                      ) : (
                        <p
                          key={`message-${message?.time} ${messageIndex}`}
                          className="chatText chatText--right"
                        >
                          <span className="chatText__value">
                            {message?.message}
                          </span>
                          <span className="chatText__time">
                            {moment(message?.time).isSame(moment(), "day")
                              ? `Today, ${moment(message?.time)?.format(
                                  "h:mma"
                                )}`
                              : moment(message?.time)?.format(
                                  "DD/MM/YYYY h:mma"
                                )}
                          </span>
                        </p>
                      )
                    )
                  : null}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
